import { HashOf, Nullable } from '@jamesgmarks/utilities';
import { IClientHasBillingContacts, IOwnershipGroups } from '@llws/lift-entity-interfaces';
import { IInvoice } from '../../../entity-interfaces/IInvoice';

/**
 * The account-level data `GET /invoices/generated_invoices` returns alongside a page of invoices, in
 * `meta.custom`.
 *
 * Billing contacts and ownership groups belong to the account, not to the invoice, so the endpoint
 * sends each of them once per response instead of repeating them on every invoice — a wide date range
 * on a large account used to repeat them thousands of times and overflow the response.
 */
export interface IInvoiceListRelatedData {
  /** Billing contacts of each client on the page, keyed by the LIFT client id. */
  clientHasBillingContactsByClientId: HashOf<IClientHasBillingContacts[]>;
  /** Every ownership group referenced by the page's invoices, with its contacts loaded. */
  ownershipGroups: IOwnershipGroups[];
}

/**
 * Puts the account-level data back onto each invoice, giving callers the fully-populated invoices the
 * list view and the send-documents modal expect.
 *
 * The attached objects are shared between invoices rather than copied, so this stays cheap for a large
 * page of invoices.
 *
 * @param invoices The page of invoices, as returned in the response's `data`. Returned as-is when
 *   absent, so an error response still reaches the reducer unchanged.
 * @param relatedData The response's `meta.custom`. Invoices are returned untouched when it is absent.
 * @returns The invoices, each with its `ownershipGroup` and its clients' `clientHasBillingContacts`.
 */
export const attachInvoiceAccountData = (
  invoices?: Nullable<IInvoice[]>,
  relatedData?: Nullable<IInvoiceListRelatedData>,
) => {
  if (!invoices || !relatedData) {
    return invoices;
  }

  const { clientHasBillingContactsByClientId, ownershipGroups } = relatedData;
  const ownershipGroupsById = new Map((ownershipGroups ?? []).map((og) => [og.id, og]));

  return invoices.map((invoice) => {
    const ownershipGroup = (
      invoice.ownershipGroupId ? ownershipGroupsById.get(invoice.ownershipGroupId) : undefined
    );

    if (!invoice.billingAccount) {
      return { ...invoice, ownershipGroup };
    }

    return {
      ...invoice,
      ownershipGroup,
      billingAccount: {
        ...invoice.billingAccount,
        clients: (invoice.billingAccount.clients ?? []).map((client) => ({
          ...client,
          clientHasBillingContacts: clientHasBillingContactsByClientId?.[client.clientId] ?? [],
        })),
      },
    };
  });
};
